import { Dialog } from "@mui/material";
import { Info, Layers } from "lucide-react";
import { Fragment, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppForm } from "src/hooks";
import { validName } from "src/utils/name";

import { ConsentFaq } from "./consent-faq";
import { ConsentFooter } from "./consent-footer";
import { ConsentLegalNotice } from "./consent-legal-notice";
import {
  consentPreferencesSchema,
  countActivePurposes,
  createConsentPreferences,
  hasAcceptedEverything,
  type ConsentPreferences,
} from "./consent-preferences";
import { ConsentPurposeCard } from "./consent-purpose-card";
import { ConsentReassuranceNote } from "./consent-reassurance-note";
import { ConsentRefusalModal } from "./consent-refusal-modal";
import { consentDialogStyles } from "./styles";
import { useConsentContent } from "./use-consent-content";

type Props = {
  open: boolean;
  firstName: string;
  onComplete: (preferences: ConsentPreferences) => void;
};

export function ConsentPlatformModal({
  open,
  firstName,
  onComplete,
}: Props): ReactElement {
  const { t } = useTranslation();

  const [isRefusalConfirmationOpen, setIsRefusalConfirmationOpen] =
    useState(false);

  const form = useAppForm({
    defaultValues: createConsentPreferences({ isAccepted: false }),
    validators: { onSubmit: consentPreferencesSchema },
    onSubmit: ({ value }) => {
      if (countActivePurposes(value) === 0) {
        setIsRefusalConfirmationOpen(true);
        return;
      }
      onComplete(value);
    },
  });

  const { purposes, channels } = useConsentContent();

  return (
    <>
      <Dialog
        open={open && !isRefusalConfirmationOpen}
        transitionDuration={0}
        disableEscapeKeyDown
        maxWidth={false}
        className="consent-platform-dialog"
        sx={consentDialogStyles}
        aria-labelledby="consent-platform-title"
        aria-describedby="consent-platform-description"
      >
        <form
          className="consent-platform"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <div className="consent-platform-scroll">
            <header className="consent-platform-header">
              <span className="consent-platform-badge">
                <Info />
                {t("Étape obligatoire")}
              </span>
              <h1 id="consent-platform-title">
                {validName(firstName)
                  ? t(
                      "Bonjour {{firstName}}, paramétrons vos préférences de communication",
                      { firstName },
                    )
                  : t("Bonjour, paramétrons vos préférences de communication")}
              </h1>
              <p id="consent-platform-description">
                {t(
                  "Indiquez les communications que vous souhaitez recevoir et par quels canaux pour profiter de nos astuces et offres utiles à votre situation.",
                )}
              </p>
            </header>
            <div className="consent-platform-body">
              <div className="consent-platform-intro">
                <span className="consent-platform-tip-icon" aria-hidden="true">
                  <Layers />
                </span>
                <p>
                  {t(
                    "Quelques clics suffisent pour continuer à accéder à votre espace client et ne rien manquer d'essentiel : conseils pour votre logement et votre immeuble, alertes immobilières, événements près de chez vous, offres partenaires…",
                  )}
                </p>
              </div>
              <div className="consent-platform-accept-all">
                <div>
                  <span className="consent-platform-badge">
                    {t("Recommandé")}
                  </span>
                  <strong>{t("Accepter les communications")}</strong>
                  <p>
                    {t(
                      "Email (avec pixels de suivi), SMS, WhatsApp, Téléphone - Modifiable à tout moment depuis votre espace.",
                    )}
                  </p>
                </div>
                <form.Subscribe
                  selector={(state) => hasAcceptedEverything(state.values)}
                >
                  {(isEverythingAccepted) => (
                    <button
                      type="button"
                      className="consent-platform-button consent-platform-button-accept"
                      onClick={() => {
                        form.reset(
                          createConsentPreferences({ isAccepted: true }),
                        );
                      }}
                    >
                      {isEverythingAccepted
                        ? t("Tout accepté")
                        : t("Tout accepter")}
                    </button>
                  )}
                </form.Subscribe>
              </div>
              <p className="consent-platform-divider">
                {t("Ou personnalisez vos préférences")}
              </p>
              <p className="consent-platform-hint">
                <span>
                  <strong>
                    {t("Activez une finalité, tous les canaux s'allument !")}
                  </strong>{" "}
                  {t(
                    "Vous gardez la main pour décocher ceux que vous ne souhaitez pas recevoir.",
                  )}
                </span>
              </p>
              {purposes.map((purpose) => (
                <Fragment key={purpose.id}>
                  <form.Field name={purpose.id}>
                    {(field) => (
                      <ConsentPurposeCard
                        purpose={purpose}
                        channels={channels}
                        selection={field.state.value}
                        onChange={field.handleChange}
                      />
                    )}
                  </form.Field>
                  {purpose.tip && (
                    <ConsentReassuranceNote icon={purpose.tipIcon}>
                      {purpose.tip}
                    </ConsentReassuranceNote>
                  )}
                </Fragment>
              ))}
              <ConsentFaq />
              <ConsentLegalNotice />
            </div>
          </div>
          <form.Subscribe
            selector={(state) => countActivePurposes(state.values)}
          >
            {(count) => <ConsentFooter count={count} />}
          </form.Subscribe>
        </form>
      </Dialog>
      <ConsentRefusalModal
        open={open && isRefusalConfirmationOpen}
        onReview={() => setIsRefusalConfirmationOpen(false)}
        onConfirm={() => {
          setIsRefusalConfirmationOpen(false);
          onComplete(form.state.values);
        }}
      />
    </>
  );
}
